<template>
  <div class="tag-menu">
    <div class="tag-menu__inner">
      <h5 class="tag-menu__title">Edit Polygon</h5>

      <div class="tag-menu__col">
        <!-- Fill colour -->
        <div class="tag-menu__block">
          <div class="inline-label">
            <label>Fill</label>

            <div class="colour-controls">
              <div
                class="btn-colour"
                :style="{ backgroundColor: value.fill }"
                @click="toggleColourWidget('fill')"
              ></div>

              <!-- eyedropper -->
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm colour-eye-button"
                @click="pickColour('fill')"
              >
                <fa :icon="['fas', 'eye-dropper']" />
              </button>
            </div>
          </div>

          <verte
            v-if="activeColourWidget === 'fill'"
            class="colour-picker"
            display="widget"
            picker="square"
            model="rgb"
            v-model="value.fill"
            :showHistory="true"
            :colorHistory.sync="ColourHistory"
          />
        </div>

        <!-- Stroke colour -->
        <div class="tag-menu__block">
          <div class="inline-label">
            <label>Stroke</label>

            <div class="colour-controls">
              <div
                class="btn-colour"
                :style="{ backgroundColor: value.stroke }"
                @click="toggleColourWidget('stroke')"
              ></div>

              <!-- eyedropper -->
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm colour-eye-button"
                @click="pickColour('stroke')"
              >
                <fa :icon="['fas', 'eye-dropper']" />
              </button>
            </div>
          </div>

          <verte
            v-if="activeColourWidget === 'stroke'"
            class="colour-picker"
            display="widget"
            picker="square"
            model="rgb"
            v-model="value.stroke"
            :showHistory="true"
            :colorHistory.sync="ColourHistory"
          />
        </div>

        <!-- Stroke width -->
        <div class="tag-menu__block tag-menu__block--inline">
          <label>Stroke Width</label>
          <input
            id="strokeWidth"
            class="form-control"
            type="number"
            min="0"
            max="20"
            v-model.number="value.strokeWidth"
          />
        </div>

        <!-- Drop shadow -->
        <div class="tag-menu__block tag-menu__block--inline">
          <label style="flex-shrink: 0; margin-right: 10px">Drop Shadow</label>
          <toggle-button
            v-model="value.dropShadow"
            color="#28a745"
            :sync="true"
            :labels="false"
            :width="40"
            :height="20"
            style="font-weight: 400"
          />
        </div>

        <!-- Edit shape -->
        <div class="tag-menu__block tag-menu__block--inline">
          <label style="flex-shrink: 0; margin-right: 10px">Edit Shape</label>
          <toggle-button
            v-model="editShapeOn"
            color="#28a745"
            :sync="true"
            :labels="false"
            :width="40"
            :height="20"
            style="font-weight: 400"
          />
        </div>

        <div class="tag-menu__col tag-menu__col--last buttons-row">
          <button class="btn btn-remove" @click="remove">Remove</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Verte from "verte";
import "verte/dist/verte.css";
import { mapState } from "vuex";

export default {
  name: "PolygonMenu",

  components: {
    Verte,
  },

  props: {
    value: Object,
    polygonId: {
      type: String,
      required: true,
    },
    colourHistory: { type: Array, default: () => [] },
  },

  data() {
    return {
      activeColourWidget: null, // 'fill' | 'stroke' | null
      editShapeOn: false, // local toggle state
      ColourHistory: [],
    };
  },

  computed: {
    ...mapState({
      editPolygonId: (state) => state.draggable.editPolygonId,
    }),
  },

  watch: {
    // Keep local toggle synced with Vuex
    editPolygonId: {
      immediate: true,
      handler(newId) {
        this.editShapeOn = newId === this.polygonId;
      },
    },

    // When user toggles from the menu, update Vuex
    editShapeOn(newVal) {
      if (newVal) {
        // turn ON edit mode for this polygon
        if (this.editPolygonId !== this.polygonId) {
          this.$store.commit("draggable/editPolygon", this.polygonId);
        }
      } else {
        // turn OFF edit mode if this polygon is currently being edited
        if (this.editPolygonId === this.polygonId) {
          this.$store.commit("draggable/editPolygon", null);
        }
      }
    },

    colourHistory: {
      immediate: true,
      handler(v) {
        this.ColourHistory = Array.isArray(v) ? [...v] : [];
      },
    },
  },

  methods: {
    toggleColourWidget(key) {
      this.activeColourWidget = this.activeColourWidget === key ? null : key;
    },

    // optional native EyeDropper support (Chromium only)
    async pickColour(key) {
      if (typeof window === "undefined" || !("EyeDropper" in window)) {
        this.$notify &&
          this.$notify({
            message: "Eyedropper not supported in this browser.",
            horizontalAlign: "center",
            verticalAlign: "top",
            type: "warning",
          });
        return;
      }

      try {
        const eyeDropper = new window.EyeDropper();
        const result = await eyeDropper.open();
        this.$set(this.value, key, result.sRGBHex);
        this.activeColourWidget = null;
      } catch (err) {
        if (err && err.name === "AbortError") return;
        console.error("Eyedropper error", err);
        this.$notify &&
          this.$notify({
            message: "Error using eyedropper.",
            horizontalAlign: "center",
            verticalAlign: "top",
            type: "danger",
          });
      }
    },

    //  Click from menu to put PolygonShape into editPolygonMode
    enterEditPolygonMode() {
      // if you're already editing this one, do nothing
      if (this.editPolygonId === this.polygonId) return;

      // adjust mutation name if yours is different
      this.$store.commit("draggable/editPolygon", this.polygonId);
    },

    remove() {
      if (this.$store.state.page.page) {
        const tag = this.$store.state.page.page.tags.find((item) => {
          return item.polygons.some(
            (polygon) => polygon._id === this.polygonId
          );
        });

        tag.polygons = tag.polygons.filter(
          (polygon) => polygon._id !== this.polygonId
        );
      }

      if (this.$route.path.startsWith("/live")) {
        // must be a more efficient way of finding the parent element
        const livingOptions = this.$store.state.living.livingOptions;
        const subsections = this.$store.state.living.subsections;
        const properties = this.$store.state.living.properties;

        const element = [...livingOptions, ...subsections, ...properties].find(
          (item) => {
            return item.polygons.some(
              (polygon) => polygon._id === this.polygonId
            );
          }
        );

        element.polygons = element.polygons.filter(
          (polygon) => polygon._id !== this.polygonId
        );
      }

      this.$store.commit("draggable/deselectAll");
    },
  },
};
</script>

<style lang="scss" scoped>
/* No extra layout styles needed – we now rely on MenuWrapper's ::v-deep .tag-menu rules */
/* Can put polygon specific changes here if needed */
</style>
